import { createSelector } from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import { RootState } from '~/store';
import { isWindows } from '~/utils/helper/device';
import tikuElementary from '/assets/image/tiku/tikuElementary.png';
import tikuJunior from '/assets/image/tiku/tikuJunior.png';
import tikuHigh from '/assets/image/tiku/tikuHigh.png';
import { FileType, TFileCatalog } from '~/store/file/file.type';
import { FileCollection, SIDEBAR } from '~/types/enum';
import { FileStatus } from '../resource/resource.type';
import { gradesChangeText } from '~/utils/constants';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';

export const getterOneBookFiles = createSelector(
  (state: RootState) => state.file.oneBookFiles,
  files => files,
);
export const getterTikuFiles = createSelector(
  (state: RootState) => state.file.tikuFiles,
  files => files,
);
export const getterFlashFiles = createSelector(
  (state: RootState) => state.file.flashFiles,
  files => files,
);
export const getterFileCatalog = createSelector(
  (state: RootState) => state.file.fileCatalog,
  catalog => catalog,
);

const getPathInfo = (exePath: string) => {
  const splitPath = isWindows ? exePath.split('\\') : exePath.split('/');
  return {
    dist: splitPath[0],
    folder: splitPath[1],
    subFolder: splitPath[2],
  };
};

const getTikuImage = (fileName: string) => {
  if (fileName.match('國小')) {
    return tikuElementary;
  } else if (fileName.match('高中')) {
    return tikuHigh;
  } else {
    return tikuJunior;
  }
};

export const getterFiles = createSelector(
  (state: RootState) => state.file,
  ({ tikuFiles, oneBookFiles, flashFiles }) => {
    const newFlashFiles = (flashFiles || []).map(file => {
      const splitItem = isWindows ? file.split('\\') : file.split('/');
      const fileNameIndex = splitItem.length - 4;
      return {
        type: FileType.flash,
        img: file,
        fileName: splitItem[fileNameIndex],
        exePath: file.replace(
          'ToolMon\\thumbnails\\nanibook.jpg',
          '\\Nani_index.exe',
        ), // 這個要確認是不是每個執行檔都是 Nani_index.exe
        opening: false,
        ...getPathInfo(file),
      };
    });

    const newOneBookFiles = (oneBookFiles || []).map(file => {
      const splitItem = isWindows ? file.split('\\') : file.split('/');
      const fileNameIndex = splitItem.length - 3;
      return {
        type: FileType.oneBook,
        img: file,
        fileName: splitItem[fileNameIndex - 3],
        exePath: file.replace('OneBook\\cover.jpg', '\\OneBook.exe'),
        opening: false,
        ...getPathInfo(file),
      };
    });

    const newTikuFiles = (tikuFiles || []).map(file => {
      const splitItem = isWindows ? file.split('\\') : file.split('/');
      const fileName = splitItem[2];
      return {
        type: FileType.tiku,
        img: getTikuImage(fileName),
        fileName,
        exePath: file,
        opening: false,
        ...getPathInfo(file),
      };
    });

    return [...newFlashFiles, ...newOneBookFiles, ...newTikuFiles];
  },
);

export const getterCollectionFiles = createSelector(
  [(state: RootState) => state.file, (state, param: FileCollection) => param],
  (fileMap, collection) => {
    if (collection === FileCollection.onebook) {
      const list = (fileMap.oneBookFiles || []).map(file => {
        const splitItem = isWindows ? file.split('\\') : file.split('/');
        const fileNameIndex = splitItem.length - 3;
        return {
          key: file,
          folderPath: splitItem
            .slice(0, splitItem.length - 2)
            .join(isWindows ? '\\' : '/'),
          type: FileType.oneBook,
          img: file,
          fileName: splitItem[fileNameIndex],
          exePath: file.replace('OneBook\\cover.jpg', '\\OneBook.exe'),
          opening: false,
          ...getPathInfo(file),
        };
      });
      return list;
    }

    if (collection === FileCollection.flash) {
      const list = (fileMap.flashFiles || []).map(file => {
        const splitItem = isWindows ? file.split('\\') : file.split('/');
        const fileNameIndex = splitItem.length - 4;
        return {
          key: file,
          folderPath: splitItem
            .slice(0, splitItem.length - 3)
            .join(isWindows ? '\\' : '/'),
          type: FileType.flash,
          img: file,
          fileName: splitItem[fileNameIndex],
          exePath: file.replace(
            'ToolMon\\thumbnails\\nanibook.jpg',
            '\\Nani_index.exe',
          ),
          opening: false,
          ...getPathInfo(file),
        };
      });

      return list;
    }

    if (collection === FileCollection.tiku) {
      const list = (fileMap.tikuFiles || []).map(file => {
        const splitItem = file.split(/[/\\]/);
        const fileName = splitItem[splitItem.length - 2] || splitItem[2];
        return {
          key: file,
          folderPath: '',
          type: FileType.tiku,
          img: getTikuImage(fileName),
          fileName,
          exePath: file,
          opening: false,
          ...getPathInfo(file),
        };
      });
      return list;
    }
    return [];
  },
);

export const getterFileSearchCondition = createSelector(
  (state: RootState) => state.file.searchCondition,
  searchCondition => {
    const searchList = [];
    for (const key of Object.keys(searchCondition)) {
      if (searchCondition[key as keyof typeof searchCondition] !== null) {
        const obj: { [key: string]: string | resourceEduCode | null } = {};
        obj[key] = searchCondition[key as keyof typeof searchCondition];
        searchList.push(obj);
      }
    }
    return searchList;
  },
);

export const getterFilterCollectionFiles = createSelector(
  [getterCollectionFiles, getterFileSearchCondition],
  (fileList, searchList) => {
    let newFileList = fileList;
    searchList.forEach(searchItem => {
      newFileList = newFileList.filter(file => {
        const searchKey = Object.keys(searchItem)[0] || '';
        const searchValue = Object.values(searchItem)[0] || '';
        if (searchKey === 'years') {
          return file.fileName.slice(0, 3).includes(searchValue);
        } else if (searchKey === 'grades') {
          const fileName = file.fileName.slice(3);
          const targets = [
            `${searchValue}上`,
            `${searchValue}下`,
            gradesChangeText[searchValue as keyof typeof gradesChangeText],
          ];
          return targets.some(target => fileName.includes(target));
        } else {
          return file.fileName.slice(3, -1).includes(searchValue);
        }
      });
    });
    return newFileList;
  },
);

export const getterMyResourcesFiles = createSelector(
  [
    (state: RootState) => state.file,
    getterFileSearchCondition,
    (state, param: FileCollection) => param,
  ],
  (fileMap, searchList) => {
    // TODO: 這裡預計要和 BookCard 相同設計，但這裡的格式還沒確認
    const list = Object.values(fileMap.fileCatalog).map(file => {
      const resource1 = file.resource1 || '';
      const resource2 = file.resource2 || resource1;
      const resource3 = file.resource3 || resource2;
      return {
        ...file,
        file: {
          fileSize: file.fileSize,
          url: file.url,
          fileUploadTime: file.fileUploadTime,
        },
        status: FileStatus.finish,
        resource1,
        resource2,
        resource3,
      };
    });
    // 排除電子書、題庫
    let filterList = list.filter(item => {
      return !(
        (item.resource1 as SIDEBAR) === SIDEBAR.ebook ||
        (item.resource1 as SIDEBAR) === SIDEBAR.tiku
      );
    });

    // 排除資料不全的紀錄
    filterList = filterList.filter(item => {
      return (
        item.year &&
        item.eduCode &&
        item.grade &&
        item.schoolYear &&
        item.resource1 &&
        item.resource2 &&
        item.resource3
      );
    });

    searchList.forEach(searchItem => {
      filterList = filterList.filter(file => {
        const searchKey = Object.keys(searchItem)[0] || '';
        const searchValue = Object.values(searchItem)[0] || '';
        switch (searchKey) {
          case 'eduCodes':
            return file.eduCode === searchValue;
          case 'years':
            return file.year.slice(0, 3) === searchValue;
          case 'grades':
            return file.grade.includes(searchValue);
          case 'subjects':
            return file.subject === searchValue;
        }
      });
    });

    const target: {
      [resource1: string]: {
        [resource2: string]: {
          [resource3: string]: TFileCatalog[];
        };
      };
    } = {};

    filterList.forEach(item => {
      const info = { resource: item };
      set(
        target,
        [item.resource1, item.resource2, item.resource3],
        [
          ...get(target, [item.resource1, item.resource2, item.resource3], []),
          info,
        ],
      );
    });

    return target;
  },
);
